import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 * @property {HTMLDivElement} overlayHost - The visual surface shared by attached header overlays
 */

/**
 * @typedef {Object} HeaderOverlayParticipant
 * @property {string} id
 * @property {(options?: { reason?: string, immediate?: boolean, restoreFocus?: boolean, event?: KeyboardEvent }) => void} close
 * @property {() => HTMLElement | null} [getPanel]
 * @property {() => HTMLElement | null} [getTrigger]
 * @property {(event: KeyboardEvent) => void} [handleEscape]
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop', 'overlayHost'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** @type {Map<object, HeaderOverlayParticipant>} */
  #overlayParticipants = new Map();

  /** @type {{ owner: object, participant: HeaderOverlayParticipant, panel: HTMLElement | null, trigger: HTMLElement | null } | null} */
  #activeOverlay = null;

  /** @type {number | null} */
  #overlayGeometryRaf = null;

  #overlayResizeObserver = new ResizeObserver(() => {
    this.#scheduleOverlayGeometry();
  });

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // Round up so overlays never overlap a fractional-pixel header bottom.
    const roundedHeaderHeight = Math.ceil(entry.borderBoxSize[0].blockSize);
    document.documentElement.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    this.#alignCompactSubmenus();
    this.#scheduleOverlayGeometry();
  });

  /** Aligns each compact desktop submenu directly below the full header. */
  #alignCompactSubmenus = () => {
    if (window.innerWidth < 750) return;

    const headerBottom = this.getBoundingClientRect().bottom;
    this.querySelectorAll('.menu-list__list-item:not([slot="overflow"])').forEach((item) => {
      const submenu = [...item.children].find((child) => child.classList?.contains('menu-list__submenu'));
      if (!submenu) return;

      const itemRect = item.getBoundingClientRect();
      submenu.style.setProperty('--monolyt-submenu-top', `${Math.round(headerBottom - itemRect.top)}px`);
      if (this.hasAttribute('data-monolyt-v2-header') && item.dataset.monolytSubmenuAlign === 'viewport-left') {
        submenu.style.setProperty('--monolyt-submenu-left', `${-itemRect.left}px`);
      }
    });

    this.querySelectorAll('dropdown-localization-component').forEach((component) => {
      const componentTop = component.getBoundingClientRect().top;
      component.style.setProperty('--monolyt-localization-top', `${Math.round(headerBottom - componentTop)}px`);
    });
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  /**
   * Registers an overlay without moving its Shopify-owned DOM.
   * @param {object} owner
   * @param {HeaderOverlayParticipant} participant
   * @returns {() => void}
   */
  registerOverlay(owner, participant) {
    this.#overlayParticipants.set(owner, participant);

    return () => {
      if (this.#activeOverlay?.owner === owner) {
        this.releaseOverlay(owner);
      }
      this.#overlayParticipants.delete(owner);
    };
  }

  /**
   * Gives one participant exclusive ownership of the shared overlay surface.
   * @param {object} owner
   * @param {{ panel?: HTMLElement | null, trigger?: HTMLElement | null }} [overrides]
   * @returns {boolean}
   */
  claimOverlay(owner, overrides = {}) {
    const participant = this.#overlayParticipants.get(owner);
    if (!participant) return false;

    const previous = this.#activeOverlay;
    if (previous && previous.owner !== owner) {
      this.#activeOverlay = null;
      this.refs.overlayHost.hidden = true;
      previous.trigger?.setAttribute('aria-expanded', 'false');
      this.#overlayResizeObserver.disconnect();
      previous.participant.close({ reason: 'switch', immediate: true, restoreFocus: false });
    } else if (previous?.trigger && previous.trigger !== overrides.trigger) {
      previous.trigger.setAttribute('aria-expanded', 'false');
    }

    const panel = overrides.panel ?? participant.getPanel?.() ?? null;
    const trigger = overrides.trigger ?? participant.getTrigger?.() ?? null;

    this.#activeOverlay = { owner, participant, panel, trigger };
    trigger?.setAttribute('aria-expanded', 'true');
    this.dataset.overlaySource = participant.id;

    this.#overlayResizeObserver.disconnect();
    if (panel) this.#overlayResizeObserver.observe(panel);

    this.#scheduleOverlayGeometry();
    return true;
  }

  /**
   * Releases the shared surface if the calling participant still owns it.
   * @param {object} owner
   * @param {{ restoreFocus?: boolean }} [options]
   */
  releaseOverlay(owner, options = {}) {
    const active = this.#activeOverlay;
    if (!active || active.owner !== owner) return;

    this.#activeOverlay = null;
    this.#overlayResizeObserver.disconnect();
    active.trigger?.setAttribute('aria-expanded', 'false');

    if (this.#overlayGeometryRaf !== null) {
      cancelAnimationFrame(this.#overlayGeometryRaf);
      this.#overlayGeometryRaf = null;
    }

    const host = this.refs.overlayHost;
    host.hidden = true;
    host.style.removeProperty('--monolyt-header-overlay-inline-start');
    host.style.removeProperty('--monolyt-header-overlay-width');
    host.style.removeProperty('--monolyt-header-overlay-height');
    delete host.dataset.monolytInlineEndAttached;
    delete this.dataset.overlaySource;

    if (options.restoreFocus && active.trigger?.isConnected) {
      active.trigger.focus({ preventScroll: true });
    }
  }

  /**
   * Re-measures the active participant after it changes content or visibility.
   * @param {object} owner
   */
  syncOverlayGeometry(owner) {
    if (this.#activeOverlay?.owner !== owner) return;
    this.#scheduleOverlayGeometry();
  }

  /**
   * Closes whichever participant currently owns the shared surface.
   * @param {{ reason?: string, immediate?: boolean, restoreFocus?: boolean, event?: KeyboardEvent }} [options]
   */
  closeActiveOverlay(options = {}) {
    const active = this.#activeOverlay;
    if (!active) return;
    active.participant.close(options);
  }

  #scheduleOverlayGeometry = () => {
    if (!this.#activeOverlay || this.#overlayGeometryRaf !== null) return;

    this.#overlayGeometryRaf = requestAnimationFrame(() => {
      this.#overlayGeometryRaf = null;
      this.#syncOverlayGeometry();
    });
  };

  #syncOverlayGeometry() {
    const active = this.#activeOverlay;
    if (!active) return;

    const panel = active.participant.getPanel?.() ?? active.panel;
    if (!(panel instanceof HTMLElement) || !panel.isConnected) return;

    if (panel !== active.panel) {
      active.panel = panel;
      this.#overlayResizeObserver.disconnect();
      this.#overlayResizeObserver.observe(panel);
    }

    const panelRect = panel.getBoundingClientRect();
    if (panelRect.width <= 0 || panelRect.height <= 0) return;

    const headerRect = this.getBoundingClientRect();
    const panelStyle = getComputedStyle(panel);
    const transform = panelStyle.transform === 'none' ? null : new DOMMatrixReadOnly(panelStyle.transform);
    const panelLeft = panelRect.left - (transform?.m41 ?? 0);
    const panelTop = panelRect.top - (transform?.m42 ?? 0);
    const panelRight = panelLeft + panelRect.width;
    const panelBottom = panelTop + panelRect.height;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const inlineStart = isRtl ? headerRect.right - panelRight : panelLeft - headerRect.left;
    const attachedHeight = Math.max(panelRect.height, panelBottom - headerRect.bottom);
    const host = this.refs.overlayHost;

    host.style.setProperty('--monolyt-header-overlay-inline-start', `${inlineStart}px`);
    host.style.setProperty('--monolyt-header-overlay-width', `${panelRect.width}px`);
    host.style.setProperty('--monolyt-header-overlay-height', `${attachedHeight}px`);
    host.toggleAttribute('data-monolyt-inline-end-attached', Math.abs(panelRight - headerRect.right) <= 1);
    host.hidden = false;
  }

  #handleOverlayKeyDown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key !== 'Escape' || !this.#activeOverlay) return;

    event.preventDefault();
    if (this.#activeOverlay.participant.handleEscape) {
      this.#activeOverlay.participant.handleEscape(event);
      return;
    }

    this.closeActiveOverlay({ reason: 'escape', restoreFocus: true, event });
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('pointerover', this.#alignCompactSubmenus);
    this.addEventListener('focusin', this.#alignCompactSubmenus);
    window.addEventListener('resize', this.#alignCompactSubmenus, { passive: true });
    document.addEventListener('keydown', this.#handleOverlayKeyDown);
    requestAnimationFrame(this.#alignCompactSubmenus);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('pointerover', this.#alignCompactSubmenus);
    this.removeEventListener('focusin', this.#alignCompactSubmenus);
    window.removeEventListener('resize', this.#alignCompactSubmenus);
    document.removeEventListener('keydown', this.#handleOverlayKeyDown);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    this.#overlayResizeObserver.disconnect();
    this.#overlayParticipants.clear();
    this.#activeOverlay = null;
    if (this.#overlayGeometryRaf !== null) {
      cancelAnimationFrame(this.#overlayGeometryRaf);
      this.#overlayGeometryRaf = null;
    }
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length) return;

      const headerGroupHeight = [...headerGroup.children].reduce((totalHeight, element) => {
        if (!(element instanceof HTMLElement) || element.hidden) return totalHeight;
        const style = window.getComputedStyle(element);
        if (style.display === 'none') return totalHeight;
        return totalHeight + element.getBoundingClientRect().height;
      }, 0);
      // Round up so fixed overlays start below the rendered header, including fractional pixels.
      const roundedHeaderGroupHeight = Math.ceil(headerGroupHeight);
      const headerHeight = Math.ceil(header?.getBoundingClientRect().height ?? 0);
      const headerStackHeight = Math.max(headerHeight, roundedHeaderGroupHeight);

      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
      document.documentElement.style.setProperty('--header-stack-height', `${headerStackHeight}px`);
      document.body.style.setProperty('--header-height', `${headerHeight}px`);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
      document.body.style.setProperty('--header-stack-height', `${headerStackHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }

  const root = document.documentElement;
  const body = document.body;
  const visualViewport = window.visualViewport;
  let viewportGeometryRaf = null;
  let viewportGeometrySettleTimeout = null;

  const updateViewportGeometry = () => {
    viewportGeometryRaf = null;

    const isMobileDocumentRoute =
      body.dataset.monolytNavigation === 'document' && window.matchMedia('(max-width: 749px)').matches;
    const isDefaultScale = !visualViewport || Math.abs(visualViewport.scale - 1) < 0.01;

    if (!isMobileDocumentRoute || !visualViewport || !isDefaultScale) {
      root.style.removeProperty('--monolyt-visual-viewport-top');
      root.style.removeProperty('--monolyt-visual-viewport-height');
      return;
    }

    const headerGroupHeight = Math.max(0, headerGroup?.getBoundingClientRect().height ?? 0);
    const viewportTop = Math.max(0, visualViewport.offsetTop);
    const viewportHeight = Math.max(headerGroupHeight, visualViewport.height);
    const roundToDeviceStep = (value) => Math.round(value * 64) / 64;

    root.style.setProperty(
      '--monolyt-visual-viewport-top',
      `${roundToDeviceStep(viewportTop)}px`
    );
    root.style.setProperty(
      '--monolyt-visual-viewport-height',
      `${roundToDeviceStep(viewportHeight)}px`
    );
  };

  const scheduleViewportGeometry = () => {
    if (viewportGeometryRaf !== null) return;

    viewportGeometryRaf = requestAnimationFrame(updateViewportGeometry);
  };

  const scheduleSettledViewportGeometry = () => {
    scheduleViewportGeometry();

    if (viewportGeometrySettleTimeout !== null) {
      clearTimeout(viewportGeometrySettleTimeout);
    }

    viewportGeometrySettleTimeout = window.setTimeout(() => {
      viewportGeometrySettleTimeout = null;
      scheduleViewportGeometry();
    }, 180);
  };

  visualViewport?.addEventListener('resize', scheduleSettledViewportGeometry, { passive: true });
  visualViewport?.addEventListener('scroll', scheduleSettledViewportGeometry, { passive: true });
  visualViewport?.addEventListener('scrollend', scheduleViewportGeometry, { passive: true });
  window.addEventListener('scroll', scheduleViewportGeometry, { passive: true });
  window.addEventListener('resize', scheduleSettledViewportGeometry, { passive: true });
  window.addEventListener('pageshow', scheduleSettledViewportGeometry, { passive: true });
  window.addEventListener('orientationchange', scheduleSettledViewportGeometry, { passive: true });
  document.addEventListener('focusin', scheduleSettledViewportGeometry);
  document.addEventListener('focusout', scheduleSettledViewportGeometry);
  document.addEventListener('shopify:section:load', scheduleSettledViewportGeometry);
  scheduleViewportGeometry();
});
